'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronRight, File as FileIcon, Folder, FolderOpen } from 'lucide-react'
import * as React from 'react'

import { Badge } from '../badge/Badge'
import { cn } from '../lib/cn'

export interface TreeNodeData {
  /** Stable, unique id used for selection + expansion state. */
  id: string
  label: React.ReactNode
  /** Optional icon override for this node (falls back to branch/leaf defaults). */
  icon?: React.ElementType
  /** Optional class name for the icon (defaults to text-muted-foreground). */
  iconClassName?: string
  children?: TreeNodeData[]
  /** Arbitrary right-aligned content (e.g. a status Badge). Takes precedence over `count`. */
  trailing?: React.ReactNode
  /** Convenience trailing count — rendered as a Badge when > 0. */
  count?: number
  /** Collapsed initially when explicitly false; branches default to expanded. */
  defaultExpanded?: boolean
  disabled?: boolean
}

const treeItemVariants = cva('flex items-center gap-1.5 rounded-md py-1.5 pr-2 text-sm transition-colors', {
  variants: {
    selected: {
      true: 'bg-primary/10 font-medium text-primary',
      false: 'text-foreground hover:bg-accent',
    },
    disabled: {
      true: 'pointer-events-none opacity-50',
      false: '',
    },
  },
  defaultVariants: { selected: false, disabled: false },
})

export interface TreeIcons {
  branch?: React.ElementType
  branchOpen?: React.ElementType
  leaf?: React.ElementType
}

export interface TreeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>, VariantProps<typeof treeItemVariants> {
  data: TreeNodeData[]
  selectedId?: string | null
  onSelect?: (node: TreeNodeData) => void
  /** Hide the folder/file icons (labels only). */
  showIcons?: boolean
  /** Override the default folder/folder-open/file icons. */
  icons?: TreeIcons
  'aria-label'?: string
}

// Collect ids of branches that should start expanded (default: all branches).
function collectExpanded(nodes: TreeNodeData[], acc: Set<string>): Set<string> {
  for (const node of nodes) {
    const hasChildren = !!node.children?.length
    if (hasChildren && node.defaultExpanded !== false) {
      acc.add(node.id)
    }
    if (hasChildren) {
      collectExpanded(node.children!, acc)
    }
  }

  return acc
}

/**
 * Generic, dependency-free tree view (shadcn file-tree style): chevron toggle,
 * folder/file icons, connecting guide lines. Expansion is uncontrolled; selection
 * is controlled via `selectedId` + `onSelect`.
 */
export const Tree = React.forwardRef<HTMLDivElement, TreeProps>(
  ({ data, selectedId, onSelect, showIcons = true, icons, className, ...props }, ref) => {
    const [expanded, setExpanded] = React.useState<Set<string>>(() => collectExpanded(data, new Set()))

    const toggle = React.useCallback((id: string) => {
      setExpanded((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }

        return next
      })
    }, [])

    return (
      <div ref={ref} role='tree' className={cn('flex flex-col', className)} {...props}>
        {data.map((node) => (
          <TreeItem
            key={node.id}
            node={node}
            selectedId={selectedId}
            onSelect={onSelect}
            showIcons={showIcons}
            icons={icons}
            expanded={expanded}
            onToggle={toggle}
          />
        ))}
      </div>
    )
  },
)
Tree.displayName = 'Tree'

interface TreeItemProps {
  node: TreeNodeData
  selectedId?: string | null
  onSelect?: (node: TreeNodeData) => void
  showIcons: boolean
  icons?: TreeIcons
  expanded: Set<string>
  onToggle: (id: string) => void
}

const TreeItem = React.memo<TreeItemProps>(({ node, selectedId, onSelect, showIcons, icons, expanded, onToggle }) => {
  const hasChildren = !!node.children?.length
  const isOpen = expanded.has(node.id)
  const isSelected = node.id === selectedId

  const branchIcon = isOpen ? (icons?.branchOpen ?? FolderOpen) : (icons?.branch ?? Folder)
  const NodeIcon = node.icon ?? (hasChildren ? branchIcon : (icons?.leaf ?? FileIcon))

  const trailing =
    node.trailing ??
    (node.count && node.count > 0 ? (
      <Badge variant='warning' size='tag' shape='tag' className='ml-auto'>
        {node.count}
      </Badge>
    ) : null)

  return (
    <div role='treeitem' aria-expanded={hasChildren ? isOpen : undefined} aria-selected={isSelected}>
      <div className={cn(treeItemVariants({ selected: isSelected, disabled: node.disabled }))}>
        {hasChildren ? (
          <button
            type='button'
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            onClick={() => onToggle(node.id)}
            className='flex size-4 shrink-0 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground'
          >
            <ChevronRight className={cn('size-4 transition-transform', isOpen && 'rotate-90')} />
          </button>
        ) : (
          <span className='size-4 shrink-0' aria-hidden />
        )}

        <button
          type='button'
          disabled={node.disabled}
          onClick={() => onSelect?.(node)}
          className='flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left'
        >
          {showIcons && <NodeIcon className={cn('size-4 shrink-0', node.iconClassName ?? 'text-muted-foreground')} />}
          <span className='truncate'>{node.label}</span>
          {trailing}
        </button>
      </div>

      {hasChildren && isOpen && (
        <div role='group' className='ml-2 border-l border-input pl-2'>
          {node.children!.map((child) => (
            <TreeItem
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              showIcons={showIcons}
              icons={icons}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  )
})
TreeItem.displayName = 'TreeItem'

export { treeItemVariants }
